import type { LucideIcon } from 'lucide-react';
import { CreditCard, Receipt, ShoppingCart } from 'lucide-react';
import type { PermissionKey } from '@supernova/shared';
import { TAB_PERMISSIONS } from '@/hooks/use-permissions';

/**
 * The home pages a user can land on. Each is tied to one business module and
 * only offered where that module is active for the selected location (and the
 * role can see it). To add another module home page: add an entry here, a
 * matching view in home-page.tsx's HOME_VIEWS, and the same id → module pair in
 * apps/api/src/routes/user-preferences.ts.
 *
 * Order matters: with no saved preference the first available page wins, so
 * the POS dashboard stays the default wherever POS Management is on — the
 * behaviour the page had before module home pages existed.
 */
export type HomePageId = 'pos' | 'receive-payments' | 'send-payments';

export interface HomePageDef {
  id: HomePageId;
  label: string;
  /** One line under the label in the settings picker. */
  description: string;
  /** `tenant_modules.module_name` that has to be active for the location. */
  moduleName: string;
  icon: LucideIcon;
  /** Roles that can use this page. */
  roles: readonly string[];
  /** On top of the role, the user needs one of these (its summary endpoint accepts them). */
  permissions?: readonly PermissionKey[];
}

/**
 * Module home pages are business overviews, so they follow the same floor as
 * the POS analytics: Owner and Admin (plus the platform super admin). Managers
 * and employees keep the Quick Links grid — no analytics for them.
 */
export const HOME_PAGE_ROLES = ['super_admin', 'owner', 'admin'] as const;

export const HOME_PAGES: readonly HomePageDef[] = [
  {
    id: 'pos',
    label: 'POS Management',
    description: 'Sales, orders and product performance from your POS',
    moduleName: 'products',
    icon: ShoppingCart,
    roles: HOME_PAGE_ROLES,
  },
  {
    id: 'receive-payments',
    label: 'Receive Payments',
    description: 'Receivables, collections and customer payments',
    moduleName: 'collections',
    icon: Receipt,
    roles: HOME_PAGE_ROLES,
    permissions: [...TAB_PERMISSIONS.receivePayments, 'reports.rp.view'],
  },
  {
    id: 'send-payments',
    label: 'Send Payments',
    description: 'Vendor payments, bills and payees',
    moduleName: 'payables',
    icon: CreditCard,
    roles: HOME_PAGE_ROLES,
    permissions: [...TAB_PERMISSIONS.sendPayments, 'reports.sp.view'],
  },
];

export const HOME_PAGE_IDS: readonly HomePageId[] = HOME_PAGES.map((p) => p.id);

export function isHomePageId(value: unknown): value is HomePageId {
  return typeof value === 'string' && (HOME_PAGE_IDS as readonly string[]).includes(value);
}

export function homePageAllowsRole(page: HomePageDef, role: string | undefined): boolean {
  return !!role && page.roles.includes(role);
}

/** The user holds one of the page's permissions (pages without any always pass). */
export function homePageAllowsPermissions(
  page: HomePageDef,
  hasAny: (...keys: readonly PermissionKey[]) => boolean,
): boolean {
  return !page.permissions || hasAny(...page.permissions);
}

/**
 * Home pages this user can use at a location: module active there, role
 * allowed and, when given, one of the page's permissions held. Registry order
 * is kept so callers can take the first as the fallback.
 */
export function availableHomePages(
  modules: ReadonlyArray<{ moduleName: string; isActive: boolean }>,
  role: string | undefined,
  hasAny?: (...keys: readonly PermissionKey[]) => boolean,
): HomePageDef[] {
  const active = new Set(modules.filter((m) => m.isActive).map((m) => m.moduleName));
  return HOME_PAGES.filter(
    (p) => active.has(p.moduleName) && homePageAllowsRole(p, role) && (!hasAny || homePageAllowsPermissions(p, hasAny)),
  );
}

/**
 * The page to show: the saved preference when it's available here, otherwise
 * the first available page, otherwise null (the caller shows the shortcut
 * grid). A preference for a module that's off at this location is simply
 * ignored rather than erroring — the user may well have it on elsewhere.
 */
export function resolveHomePage(available: readonly HomePageDef[], preferred: unknown): HomePageDef | null {
  if (isHomePageId(preferred)) {
    const match = available.find((p) => p.id === preferred);
    if (match) return match;
  }
  return available[0] ?? null;
}
